import { useRef, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Camera, Upload, Plus, Trash2, RefreshCcw, CheckCircle2, AlertTriangle, X,
  Loader2, MessageSquare, Phone, HelpCircle, Image as ImageIcon, FolderOpen,
  ImagePlus,
} from 'lucide-react';
import { uploadFiles, validateImage } from '@/lib/api';

/**
 * Opening-based photo intake.
 *
 * Replaces the rigid 2/4 property-side photo flow with an unlimited list of
 * "openings" (one window/door per card). Each opening carries:
 *  - photo (required)
 *  - opening_type (required, default Window)
 *  - approx width / height in inches (optional)
 *  - notes (optional)
 *
 * Plus an optional `overview_photos` section for wide/contextual shots.
 *
 * IMPORTANT business rules:
 *  - We do NOT call this "AI measurement". Rough W/H are user-entered estimates.
 *  - All UI says: "Rough measurements are optional and used for estimating only.
 *    Final measurements are confirmed before ordering."
 *  - The forbidden words `final / exact / order-ready / guaranteed` are not used.
 */

const OPENING_TYPES = [
  'Window',
  'Sliding Glass Door',
  'Front Door',
  'French Door',
  'Fixed Glass',
  'Other',
];

const trackEvent = (eventName, data = {}) => {
  if (typeof window === 'undefined') return;
  if (window.gtag) window.gtag('event', eventName, { event_category: 'opening_intake', ...data });
  if (window.fbq) {
    try { window.fbq('trackCustom', eventName, data); } catch (err) { console.warn('fbq trackCustom failed:', err); }
  }
};

const newId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `o_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

const validateImageForOpening = async (fileId) => {
  // Use the existing validator with view='opening' so the prompt is opening-mode
  // (lenient, focused on a single window/door rather than a property exterior).
  try {
    const resp = await validateImage(fileId, 'single_family', 'opening');
    return resp.data || null;
  } catch (e) {
    // Fail-safe: don't block on validator outage
    return { status: 'accept', message: 'Photo added.', _fallback: true };
  }
};

const validateImageForOverview = async (fileId) => {
  try {
    const resp = await validateImage(fileId, 'single_family', 'overview');
    return resp.data || null;
  } catch (e) {
    return { status: 'accept', message: 'Photo added.', _fallback: true };
  }
};

// ---------- Photo Help fallback ----------
const HELP_PHONE_TEL = 'tel:8557847627';
const HELP_PHONE_SMS = 'sms:8557847627';

function PhotoHelpBlock() {
  const scheduleUrl = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_HELP_CALL_URL)
    ? process.env.REACT_APP_HELP_CALL_URL
    : HELP_PHONE_TEL;

  return (
    <Card
      className="rounded-2xl border border-[hsl(var(--border))] bg-[var(--brand-ocean-100)]/20"
      data-testid="photo-help-block"
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-[var(--brand-ocean-100)] flex items-center justify-center shrink-0">
            <HelpCircle className="h-5 w-5 text-[var(--brand-ocean-700)]" />
          </div>
          <div className="flex-1">
            <h4 className="font-heading font-semibold text-base text-[var(--brand-slate-900)]">
              Need help with photos?
            </h4>
            <p className="text-sm text-[var(--brand-slate-700)] mt-0.5">
              Schedule a quick quote help call. No sales pitch — just help getting your quote started.
            </p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <a
                href={HELP_PHONE_SMS}
                onClick={() => trackEvent('photo_help_text_click')}
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)] font-semibold text-sm h-11 px-4 hover:bg-[var(--brand-ocean-100)]/40 transition-colors"
                data-testid="photo-help-text-btn"
              >
                <MessageSquare className="h-4 w-4" />
                Text Us for Help
              </a>
              <a
                href={scheduleUrl}
                onClick={() => trackEvent('photo_help_schedule_click', { destination: scheduleUrl })}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[hsl(var(--primary))] text-white font-semibold text-sm h-11 px-4 hover:bg-[hsl(var(--primary))]/90 transition-colors"
                data-testid="photo-help-schedule-btn"
                target={scheduleUrl.startsWith('http') ? '_blank' : undefined}
                rel={scheduleUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
              >
                <Phone className="h-4 w-4" />
                Schedule Help Call
              </a>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- Overview photo card ----------
function OverviewPhotosSection({ overviews, setOverviews }) {
  const inputRef = useRef(null);

  const addOverview = useCallback(async (file) => {
    if (!file) return;
    const id = newId();
    const draft = {
      id,
      file_id: '',
      preview: URL.createObjectURL(file),
      validation: null,
      uploading: true,
    };
    setOverviews(prev => [...prev, draft]);
    try {
      const upResp = await uploadFiles([file], '', 'overview');
      const fileId = upResp.data.files[0].id;
      // light-touch validation (lenient: overview shots are just context)
      const validation = await validateImageForOverview(fileId);
      setOverviews(prev =>
        prev.map(o => (o.id === id ? { ...o, file_id: fileId, validation, uploading: false } : o))
      );
      trackEvent('overview_photo_uploaded');
      if (validation?.status === 'reject') {
        toast.error(validation.message || 'That photo looks unrelated. Please try another.');
      } else if (validation?.status === 'warning') {
        toast.warning(validation.message || 'Overview photo added — quality is okay.');
      } else {
        toast.success('Overview photo added.');
      }
    } catch (e) {
      setOverviews(prev =>
        prev.map(o => (o.id === id ? { ...o, uploading: false, validation: { status: 'accept', message: 'Saved.' } } : o))
      );
      toast.success('Overview photo added.');
    }
  }, [setOverviews]);

  const removeOverview = (id) => {
    setOverviews(prev => prev.filter(o => o.id !== id));
    trackEvent('overview_photo_removed');
  };

  return (
    <Card className="rounded-2xl border border-[hsl(var(--border))]" data-testid="overview-photos-section">
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-10 w-10 rounded-xl bg-[hsl(var(--secondary))] flex items-center justify-center shrink-0">
            <ImagePlus className="h-5 w-5 text-[var(--brand-slate-700)]" />
          </div>
          <div className="flex-1">
            <h4 className="font-heading font-semibold text-base text-[var(--brand-slate-900)]">
              Add an overview photo <span className="text-[var(--brand-slate-500)] text-sm font-normal">(optional)</span>
            </h4>
            <p className="text-sm text-[var(--brand-slate-700)] mt-0.5">
              An overview photo helps us understand the layout, access, and project area. This is optional.
            </p>
          </div>
        </div>

        {overviews.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            {overviews.map((o) => (
              <div key={o.id} className="relative rounded-lg overflow-hidden border-2 border-[hsl(var(--border))] aspect-square bg-[hsl(var(--secondary))]">
                {o.preview ? (
                  <img src={o.preview} alt="Overview" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="h-8 w-8 text-[var(--brand-slate-400)]" />
                  </div>
                )}
                {o.uploading && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 text-white animate-spin" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeOverview(o.id)}
                  className="absolute top-1 right-1 h-7 w-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"
                  data-testid={`overview-remove-${o.id}`}
                  aria-label="Remove overview photo"
                >
                  <X className="h-4 w-4 text-[var(--brand-slate-700)]" />
                </button>
              </div>
            ))}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="overview-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) addOverview(f);
            e.target.value = '';
          }}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          className="rounded-xl h-11 border-2"
          data-testid="add-overview-btn"
        >
          <ImagePlus className="h-4 w-4 mr-2" />
          {overviews.length === 0 ? 'Add overview photo' : 'Add another overview photo'}
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------- One opening card ----------
function OpeningCard({ opening, index, onUpdate, onRemove }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    onUpdate({ preview, file_id: '', validation: null, uploading: true });
    try {
      const upResp = await uploadFiles([file], '', `opening_${index + 1}`);
      const fileId = upResp.data.files[0].id;
      const validation = await validateImageForOpening(fileId);
      onUpdate({ file_id: fileId, validation, uploading: false });
      trackEvent('opening_photo_uploaded', { opening_number: index + 1, status: validation?.status });
      if (validation?.status === 'reject') {
        toast.error(validation.message || 'That photo doesn’t look like a window or door. Please retake.');
      } else if (validation?.status === 'warning') {
        toast.warning(validation.message || 'Photo could be clearer — you can continue or retake.');
      } else {
        toast.success(validation?.message || 'Photo added.');
      }
    } catch (e) {
      onUpdate({ uploading: false, validation: { status: 'accept', message: 'Photo added.', _fallback: true } });
      toast.success('Photo added.');
    }
  }, [index, onUpdate]);

  const retake = () => {
    onUpdate({ preview: '', file_id: '', validation: null });
  };

  const status = opening.validation?.status;
  const banner =
    status === 'reject' ? 'border-red-500 bg-red-50'
    : status === 'warning' ? 'border-yellow-500 bg-yellow-50'
    : status === 'accept' && opening.file_id ? 'border-green-500 bg-green-50'
    : 'border-[hsl(var(--border))] bg-white';

  return (
    <Card
      className={`rounded-2xl border-2 ${banner} transition-colors`}
      data-testid={`opening-card-${index + 1}`}
    >
      <CardContent className="p-4 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-[var(--brand-ocean-600)] text-white text-sm font-semibold flex items-center justify-center">
              {index + 1}
            </div>
            <h4 className="font-heading font-semibold text-base text-[var(--brand-slate-900)]">
              Opening {index + 1}
            </h4>
            {opening.opening_type && (
              <Badge variant="outline" className="text-xs border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)]">
                {opening.opening_type}
              </Badge>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-9 px-2"
            data-testid={`opening-remove-${index + 1}`}
            aria-label="Remove opening"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Photo block */}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          data-testid={`opening-camera-${index + 1}`}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            e.target.value = '';
          }}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`opening-gallery-${index + 1}`}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            e.target.value = '';
          }}
        />

        {!opening.preview && !opening.file_id ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button
              type="button"
              onClick={() => cameraRef.current?.click()}
              className="rounded-xl h-12 bg-[hsl(var(--primary))] text-white font-semibold"
              data-testid={`opening-take-photo-${index + 1}`}
            >
              <Camera className="h-4 w-4 mr-2" /> Take Photo
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => galleryRef.current?.click()}
              className="rounded-xl h-12 border-2"
              data-testid={`opening-upload-photo-${index + 1}`}
            >
              <FolderOpen className="h-4 w-4 mr-2" /> Upload from Gallery
            </Button>
          </div>
        ) : (
          <div className="rounded-xl overflow-hidden border-2 border-[hsl(var(--border))] bg-black/5">
            <div className="relative">
              <img
                src={opening.preview}
                alt={`Opening ${index + 1}`}
                className="w-full h-48 sm:h-56 object-cover"
              />
              {opening.uploading && (
                <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-white animate-spin" />
                </div>
              )}
            </div>
            {opening.validation && !opening.uploading && (
              <div className={`p-3 text-sm flex items-start gap-2 ${
                status === 'reject' ? 'bg-red-100 text-red-800'
                : status === 'warning' ? 'bg-yellow-100 text-yellow-800'
                : 'bg-green-100 text-green-800'
              }`}>
                {status === 'reject' ? <X className="h-4 w-4 mt-0.5 shrink-0" />
                  : status === 'warning' ? <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  : <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />}
                <span className="flex-1">
                  {opening.validation.message || (
                    status === 'accept' ? 'Looks good.'
                    : status === 'warning' ? 'You can continue or retake.'
                    : 'Please retake this photo.'
                  )}
                </span>
              </div>
            )}
            <div className="flex gap-2 p-2 bg-white border-t">
              <Button
                type="button"
                variant="outline"
                onClick={retake}
                className="flex-1 rounded-lg h-10"
                data-testid={`opening-retake-${index + 1}`}
              >
                <RefreshCcw className="h-4 w-4 mr-1.5" /> Retake
              </Button>
              {status === 'warning' && (
                <Button
                  type="button"
                  onClick={() => onUpdate({ validation: { ...opening.validation, status: 'accept', _userOverride: true } })}
                  className="flex-1 rounded-lg h-10 bg-yellow-600 hover:bg-yellow-700 text-white"
                  data-testid={`opening-continue-anyway-${index + 1}`}
                >
                  Continue Anyway
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Type select */}
        <div>
          <Label className="text-sm font-semibold text-[var(--brand-slate-800)]">Opening Type</Label>
          <Select
            value={opening.opening_type || 'Window'}
            onValueChange={(v) => {
              onUpdate({ opening_type: v });
              trackEvent('opening_edited', { field: 'opening_type', value: v });
            }}
          >
            <SelectTrigger className="mt-1.5 h-12" data-testid={`opening-type-select-${index + 1}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPENING_TYPES.map(t => (
                <SelectItem key={t} value={t} data-testid={`opening-type-option-${t.toLowerCase().replace(/\s+/g, '-')}`}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Optional measurements */}
        <div>
          <div className="flex items-center justify-between gap-2">
            <Label className="text-sm font-semibold text-[var(--brand-slate-800)]">
              Approx. Size <span className="font-normal text-[var(--brand-slate-500)]">(optional)</span>
            </Label>
            <span className="text-xs text-[var(--brand-slate-500)]">inches</span>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            <Input
              inputMode="decimal"
              placeholder="Width"
              value={opening.width}
              onChange={(e) => onUpdate({ width: e.target.value })}
              onBlur={() => {
                if (opening.width) trackEvent('opening_measurement_entered', { field: 'width' });
              }}
              className="h-12"
              data-testid={`opening-width-${index + 1}`}
            />
            <Input
              inputMode="decimal"
              placeholder="Height"
              value={opening.height}
              onChange={(e) => onUpdate({ height: e.target.value })}
              onBlur={() => {
                if (opening.height) trackEvent('opening_measurement_entered', { field: 'height' });
              }}
              className="h-12"
              data-testid={`opening-height-${index + 1}`}
            />
          </div>
          <p className="text-[11px] text-[var(--brand-slate-500)] mt-1.5 leading-snug">
            Rough measurements are optional and used for estimating only. Final measurements are confirmed before ordering.
          </p>
        </div>

        {/* Notes */}
        <div>
          <Label className="text-sm font-semibold text-[var(--brand-slate-800)]">
            Notes <span className="font-normal text-[var(--brand-slate-500)]">(optional)</span>
          </Label>
          <Textarea
            placeholder="e.g. grids, tint, second floor, hard to access, broken glass, HOA requirement, wants same style, unsure of size"
            value={opening.notes}
            onChange={(e) => onUpdate({ notes: e.target.value })}
            onBlur={() => {
              if ((opening.notes || '').trim()) trackEvent('opening_edited', { field: 'notes' });
            }}
            rows={2}
            className="mt-1.5"
            data-testid={`opening-notes-${index + 1}`}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- Main step ----------
export default function OpeningsStep({
  openings,
  setOpenings,
  overviews,
  setOverviews,
}) {
  const addOpening = () => {
    const next = {
      id: newId(),
      file_id: '',
      preview: '',
      opening_type: 'Window',
      width: '',
      height: '',
      notes: '',
      validation: null,
      uploading: false,
    };
    setOpenings(prev => [...prev, next]);
    trackEvent('opening_added', { count: openings.length + 1 });
    // Smooth-scroll to newest card on next paint
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="opening-card-${openings.length + 1}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  // Initialize one empty opening on first render so the user has somewhere to start.
  // Effect-based init avoids the infinite-loop trap of calling a state setter during render.
  useEffect(() => {
    if (openings.length === 0) {
      setOpenings([{
        id: newId(),
        file_id: '',
        preview: '',
        opening_type: 'Window',
        width: '',
        height: '',
        notes: '',
        validation: null,
        uploading: false,
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateOpening = (id, patch) => {
    setOpenings(prev => prev.map(o => (o.id === id ? { ...o, ...patch } : o)));
    if (Object.prototype.hasOwnProperty.call(patch, 'opening_type')
        || Object.prototype.hasOwnProperty.call(patch, 'width')
        || Object.prototype.hasOwnProperty.call(patch, 'height')
        || Object.prototype.hasOwnProperty.call(patch, 'notes')) {
      // tracked via individual handlers above
    }
  };

  const removeOpening = (id) => {
    setOpenings(prev => prev.filter(o => o.id !== id));
    trackEvent('opening_removed');
  };

  const photoReadyCount = openings.filter(o => !!o.file_id && o.validation?.status !== 'reject').length;

  return (
    <div className="space-y-5 animate-fadeIn">
      {/* Header */}
      <div className="text-center mb-2">
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[var(--brand-slate-900)]">
          Add photos of the windows and doors you want quoted
        </h2>
        <p className="text-sm text-[var(--brand-slate-600)] mt-2 max-w-md mx-auto">
          One photo per opening is enough. Add as many as you need — windows, sliding doors, front doors, fixed glass.
        </p>
      </div>

      {/* Status pill */}
      <div className="flex justify-center" data-testid="openings-counter-wrap">
        <Badge
          variant="outline"
          className={`text-xs px-3 py-1 ${photoReadyCount > 0 ? 'border-green-600 text-green-700' : 'border-[var(--brand-slate-400)] text-[var(--brand-slate-600)]'}`}
          data-testid="openings-counter"
        >
          {photoReadyCount} {photoReadyCount === 1 ? 'opening' : 'openings'} added
        </Badge>
      </div>

      {/* Opening cards */}
      <div className="space-y-4">
        {openings.map((o, idx) => (
          <OpeningCard
            key={o.id}
            opening={o}
            index={idx}
            onUpdate={(patch) => updateOpening(o.id, patch)}
            onRemove={() => removeOpening(o.id)}
          />
        ))}
      </div>

      {/* Add another opening */}
      <Button
        type="button"
        onClick={addOpening}
        variant="outline"
        className="w-full rounded-xl h-12 border-2 border-dashed border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)] hover:bg-[var(--brand-ocean-100)]/40"
        data-testid="add-another-opening-btn"
      >
        <Plus className="h-4 w-4 mr-2" /> Add Another Opening
      </Button>

      {/* Photo help fallback */}
      <PhotoHelpBlock />

      {/* Optional overview photo */}
      <OverviewPhotosSection overviews={overviews} setOverviews={setOverviews} />

      {/* Disclaimer reaffirmation */}
      <p className="text-[11px] text-center text-[var(--brand-slate-500)] leading-snug max-w-md mx-auto">
        Rough measurements you provide are estimates used for quote preparation. Final measurements are confirmed before ordering.
      </p>
    </div>
  );
}
